import type { DirectoryPublic } from '@repo/types/directory';
import type { FilePublicWithPermissions } from '@repo/types/files';
import type { ErrorMessage } from '@repo/types/messages/error';
import type { Row } from '@tanstack/react-table';

import { useMemo } from 'react';

import FilesPageError from '@/components/features/files/layouts/files-page/FilePageError';
import FilesContentGrid from '@/components/features/files/layouts/files-page/FilesPageContentGrid';
import FilesPageEmpty from '@/components/features/files/layouts/files-page/FilesPageEmpty';
import FilesPageNoResults from '@/components/features/files/layouts/files-page/FilesPageNoResults';
import { useFilesStorage } from '@/store/files-store';

interface FilesStorageContentProps {
  files?: Row<FilePublicWithPermissions>[];
  directories?: Row<DirectoryPublic>[];
  isSuccess: boolean;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: ErrorMessage;
  isEmpty: boolean;
  directoryId?: string;
  handleNavigateTo: (directoryId: string) => void;
  onUploadFilesOpen: () => void;
}

export default function FilesPageContent(filesStorageContentProps: FilesStorageContentProps) {
  const { files = [], directories = [], isEmpty, isLoading, isError, errorMessage, isSuccess, handleNavigateTo, onUploadFilesOpen, directoryId } = filesStorageContentProps;

  const { setOpenCreateDir, permissions } = useFilesStorage();

  const disableActions = useMemo<boolean>(() => {
    return !permissions.write || directoryId === undefined;
  }, [permissions, directoryId]);
  const handleOnCreateDir = () => {
    setOpenCreateDir(true);
  };

  const filesArray = useMemo<FilePublicWithPermissions[]>(() => files.map((file) => file.original), [files]);
  const directoriesArray = useMemo<DirectoryPublic[]>(() => directories.map((directory) => directory.original), [directories]);

  if (isError) {
    return <FilesPageError errorMessage={errorMessage} />;
  }

  if (isSuccess && isEmpty) {
    return <FilesPageEmpty onUploadFiles={disableActions ? undefined : onUploadFilesOpen} onCreateDir={disableActions ? undefined : handleOnCreateDir} />;
  }

  if (isSuccess && filesArray.length === 0 && directoriesArray.length === 0) {
    return <FilesPageNoResults />;
  }

  return <FilesContentGrid files={filesArray} directories={directoriesArray} isSuccess={isSuccess} isLoading={isLoading} handleNavigateTo={handleNavigateTo} />;
}
